'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { inputVariants } from './input';

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  /** Debounce delay in ms. If 0 or undefined, updates are immediate. */
  debounceMs?: number;
  label?: string;
  containerClassName?: string;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      debounceMs = 0,
      label,
      placeholder,
      containerClassName,
      className,
      id,
      ...props
    },
    ref,
  ) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const commitChange = useCallback(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, [localValue, value, onChange]);

    useEffect(() => {
      if (debounceMs <= 0) return;
      const timer = setTimeout(commitChange, debounceMs);
      return () => clearTimeout(timer);
    }, [localValue, debounceMs, commitChange]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value);
      if (debounceMs <= 0) {
        onChange(e.target.value);
      }
    };

    return (
      <div className={cn('space-y-1', containerClassName)}>
        {label && (
          <label htmlFor={id} className="text-foreground block text-sm font-medium">
            {label}
          </label>
        )}
        <div className="relative">
          <MagnifyingGlassIcon
            className="text-muted pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
            aria-hidden
          />
          <input
            ref={ref}
            id={id}
            type="search"
            role="searchbox"
            value={localValue}
            onChange={handleChange}
            placeholder={placeholder}
            className={cn(inputVariants.base, 'pl-9', className)}
            {...props}
          />
        </div>
      </div>
    );
  },
);
SearchInput.displayName = 'SearchInput';
